import { useEffect, useState } from 'react';
import { Form, Modal, Input, Button, Space, Card, Radio, notification, SelectProps, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { AddTechnicalWorkScheduleReq, TechnicalWorkScheduleModel } from 'models/workSchedule';
import { UserModel } from 'models/users';
import { settingInterface } from 'DTO/interfaces';
import useTechnicalsUsers from 'hooks/query/users/useTechnicalsUsers';
import { useParams } from 'react-router-dom';
import { loadingMoreSpinner } from 'utils/ui/ScrollLoading';
import { TimePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import "./addTechnicalWorkSchedule.scss";
import { useQueryClient } from 'react-query';
import { use } from 'i18next';
import useCreateTechnicalWorkSchedule from 'hooks/query/workSchedule/useCreateTechnicalWorkSchedule';
import UseUpdateTechnicalWorkSchedule from 'hooks/query/workSchedule/UseUpdateTechnicalWorkSchedule';

const { Option } = Select;
interface AddTechnicalWorkScheduleProps {
  openAddTechnicalWorkSchedule: boolean;
  setOpenAddTechnicalWorkSchedule: (openAddOfficalHoliday: boolean) => void;
  selectedTech: TechnicalWorkScheduleModel | null
  setSelectedTech: (selectedTech: TechnicalWorkScheduleModel | null) => void
}

const options: {day: string; value: number}[] = [
    {
        day: 'monday',
        value: 1
    },
    {
        day: 'tuesday',
        value: 2
    },
    {
        day: 'wednesday',
        value: 3
    },
    {
        day: 'thursday',
        value: 4
    },
    {
        day: 'friday',
        value: 5
    },
    {
        day: 'saturday',
        value: 6
    },
    {
        day: 'sunday',
        value: 7
    },
];

const AddTechnicalWorkSchedule = ({ openAddTechnicalWorkSchedule, setOpenAddTechnicalWorkSchedule, selectedTech, setSelectedTech}: AddTechnicalWorkScheduleProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const responseCreateTechnicalWorkSchedule = useCreateTechnicalWorkSchedule();
    const responseUpdateTechnicalWorkSchedule = UseUpdateTechnicalWorkSchedule();
    const queryClient = useQueryClient()
    const [techsSetting, setTechsSetting] = useState<
        settingInterface<UserModel[]>
      >({
        page: 0,
        perPage: 0,
        loadingMore: false,
        data: [],
        search: "",
    });
    const { id } = useParams();
    const Users = useTechnicalsUsers({
        page: techsSetting.page,
        search: techsSetting.search,
        enabled: id !== "0",
    });
    const changeTechSearch = (e: any) => {
        let newArr = techsSetting;
        newArr.search = e;
        newArr.loadingMore = false;
        newArr.page = 0;
        setTechsSetting({ ...newArr });
    };
    const handleTechScroll = (event: any) => {
        let delta: any;
        let newArr = techsSetting;
        let newMin = newArr.page;
        if (event.wheelDelta) {
        delta = event.wheelDelta;
        } else {
        delta = -1 * event.deltaY;
        }
        if (!newArr.loadingMore) {
        if (delta < 0) {
            newMin = newArr.page + 1;
            let length = newArr.data.length;
            if (length !== undefined) {
            if (newMin * 4 >= length) {
                newArr.loadingMore = true;
                newArr.page = newArr.page + 1;
                Users.fetchNextPage();
            }
            }
        } else if (delta > 0 && newArr.page >= 1) {
            newMin = newArr.page - 1;
        }
        }
        newArr.page = newMin;
        setTechsSetting({ ...newArr });
    };

    useEffect(() => {
    if (Users.status === "success") {
        let allUsers: any = [];

        Users?.data?.pages &&
        Users?.data?.pages[0] !== undefined &&
        Users?.data?.pages.forEach((page) => {
            if (page !== null) allUsers = [...allUsers, ...page.data];
        });
        let newArr = techsSetting;
        newArr.data = allUsers;

        if (Users?.data?.pages[Users?.data?.pages.length - 1]?.data.length > 0) {
        newArr.loadingMore = false;
        } else {
        newArr.loadingMore = true;
        }
        setTechsSetting({ ...newArr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Users?.data?.pages]);

    useEffect(() => {
    if (!openAddTechnicalWorkSchedule) return;
    if (selectedTech) {
        form.setFieldsValue({
            user_id: selectedTech.user_id,
            day_of_week: selectedTech.day_of_week,
            start_time: selectedTech.start_time
            ? dayjs(selectedTech.start_time, "HH:mm")
            : undefined,
            end_time: selectedTech.end_time
            ? dayjs(selectedTech.end_time, "HH:mm")
            : undefined,
        });
    } 
    else {
    form.resetFields();
    }
    }, [selectedTech, openAddTechnicalWorkSchedule, form]);


    const onFinish = async (values: any) => {
        const payload: AddTechnicalWorkScheduleReq = {
            user_id: values.user_id,
            day_of_week: values.day_of_week,
            start_time: values.start_time.format("HH:mm"),
            end_time: values.end_time.format("HH:mm"),
        };
        if (selectedTech) {
            responseUpdateTechnicalWorkSchedule
                .mutateAsync(payload)
                .then(() => {
                queryClient.invalidateQueries({
                queryKey: ['technical-workSchedule'],
                });
                notification.open({
                    type: 'success',
                    message: t('technical_work_schedule_Updated_successfully'),
                });
                handleClose();
                })
                .catch(() => {});
        }
        else {
            responseCreateTechnicalWorkSchedule
                .mutateAsync(payload)
                .then(() => {
                queryClient.invalidateQueries({
                queryKey: ['technical-workSchedule'],
                });
                notification.open({
                    type: 'success',
                    message: t('technical_work_schedule_created_successfully'),
                });
                handleClose();
                })
                .catch(() => {});
        }
    };

    const handleClose = () => {
    setOpenAddTechnicalWorkSchedule(false);
    setSelectedTech(null);
    form.resetFields();
    };

    return (
        <Modal
            title={selectedTech ? t('edit_technical_work_schedule'):t('add_technical_work_schedule')}
            open={openAddTechnicalWorkSchedule}
            centered
            destroyOnClose
            onCancel={handleClose}
            closable={{ 'aria-label': 'Custom Close Button' }}
            okButtonProps={{ loading: responseCreateTechnicalWorkSchedule.isLoading || responseUpdateTechnicalWorkSchedule.isLoading }}
            onOk={() => {form.submit()}}>
            <Form form={form} onFinish={onFinish} labelCol={{ span: 24 }}>
                <div style={{marginBottom: "30px"}}>
                    <p>{t("technical_name")}</p>
                    <Form.Item
                        name="user_id"
                        rules={[{ required: true, message: t("please_select_user") }]}
                        style={{
                            margin: "0px",
                            height: "49px"}}
                        >
                        <Select
                        dropdownRender={(menu: any) => (
                            <div onWheel={handleTechScroll}>{menu}</div>
                        )}
                        filterOption={techsSetting.data as any}
                        onSearch={(e: any) => changeTechSearch(e)}
                        onBlur={(e: any) => changeTechSearch("")}
                        showSearch
                        optionFilterProp="children"
                        style={{height: "100%", margin: "0px"}}
                        >
                        {techsSetting?.data.map((tech) => (
                            <Option key={tech.id} value={tech.id}>{tech.full_name}</Option>
                        ))}
                        {(Users.isLoading || Users.isFetching) && loadingMoreSpinner}
                        </Select>
                    </Form.Item>
                </div>                
                <div style={{marginBottom: "30px"}}>
                    <p>{t("technician_working_days")}</p>
                    <Form.Item
                        name="day_of_week"
                        rules={[{ required: true, message: t("please_select_days") }]}
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            placeholder={t("please_select")}
                            options={options.map((element) => ({ label: t(element.day), value: element.value }))}
                        />
                    </Form.Item>
                </div>
                <div style={{marginBottom: "30px"}}>
                    <p>{t("technician_start_time")}</p>
                    <Form.Item
                    name="start_time"
                    rules={[{ required: true, message: t("please_select_start_time") }]}
                    style={{
                        height: "49px",
                        border: "1px solid #d9d9d9",
                        backgroundColor: "#fff",
                        padding: "0 11px",
                        boxShadow: "none",
                        transition: "none",
                        borderRadius: "10px",
                        margin: "0px",
                    }}
                    className='timePickerParent'
                    >
                    <TimePicker
                        format="HH:mm"
                        placeholder={t("start_time")}
                        style={{
                        width: "100%",
                        height: "49px",
                        backgroundColor: "transparent",
                        margin: "0",
                        }}
                        className='timePicker'
                    />
                    </Form.Item>
                </div>
                <div style={{marginBottom: "30px"}}>
                    <p>{t("technician_end_time")}</p>
                    <Form.Item
                    name="end_time"
                    dependencies={["end_time"]}
                    rules={[
                        { required: true, message: t("please_select_end_time") },
                        ({ getFieldValue }) => ({
                        validator(_, value) {
                            const start = getFieldValue("start_time");
                            if (!value || !start) return Promise.resolve();
                            if (value.isAfter(start)) return Promise.resolve();
                            return Promise.reject(
                            new Error(t("end_time_must_be_after_start_time"))
                            );
                        },
                        }),
                    ]}
                    style={{
                        height: "49px",
                        border: "1px solid #d9d9d9",
                        backgroundColor: "#fff",
                        padding: "0 11px",
                        boxShadow: "none",
                        transition: "none",
                        borderRadius: "10px",
                        margin: "0px",
                    }}
                    className='timePickerParent'
                    >
                    <TimePicker
                        format="HH:mm"
                        placeholder={t("end_time")}
                        style={{
                        width: "100%",
                        height: "49px",
                        backgroundColor: "transparent",
                        margin: "0",
                        }}
                        className='timePicker'
                    />
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
}

export default AddTechnicalWorkSchedule